/**
 * Threedium Solution Picker Component
 *
 * A searchable dropdown that allows users to browse and select
 * Threedium solutions (3D experiences) when creating new projects.
 *
 * The dropdown renders via a React portal so it is never clipped by
 * ancestor overflow:hidden containers (e.g. builder-start-screen).
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { threediumApi } from '../services/cloudApi';
import { formatErrorMessage } from '../utils/errorUtils';
import './ThreediumSolutionPicker.css';

const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
);

export function ThreediumSolutionPicker({
    value,
    onChange,
    placeholder = 'Search for a solution...',
    disabled = false
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [solutions, setSolutions] = useState([]);
    const [selectedSolution, setSelectedSolution] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasKeys, setHasKeys] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [dropdownPos, setDropdownPos] = useState(null);

    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const requestIdRef = useRef(0);
    const initialLoadDoneRef = useRef(false);

    // ── API key check (once on mount) ──────────────────────────
    useEffect(() => {
        const checkKeys = async () => {
            try {
                const status = await threediumApi.getKeysStatus();
                setHasKeys(status.has_keys && status.is_valid);
            } catch (err) {
                console.error('Failed to check Threedium keys:', err);
                setHasKeys(false);
            }
        };
        checkKeys();
    }, []);

    // ── Staleness-aware search ─────────────────────────────────
    const searchSolutions = useCallback(async (query, pageNum = 1, append = false) => {
        if (!hasKeys) return;

        const thisRequest = ++requestIdRef.current;

        setIsLoading(true);
        setError(null);

        try {
            const result = await threediumApi.listSolutions({
                search: query || undefined,
                limit: 20,
                page: pageNum
            });

            if (requestIdRef.current !== thisRequest) return;

            if (append) {
                setSolutions(prev => [...prev, ...result.solutions]);
            } else {
                setSolutions(result.solutions);
            }

            setHasMore(result.has_more);
            setPage(pageNum);
        } catch (err) {
            if (requestIdRef.current !== thisRequest) return;
            console.error('Failed to search solutions:', err);
            setError(err.message || 'Failed to load solutions');
            if (!append) setSolutions([]);
        } finally {
            if (requestIdRef.current === thisRequest) {
                setIsLoading(false);
            }
        }
    }, [hasKeys]);

    // ── Reset load-tracking when the dropdown closes ───────────
    useEffect(() => {
        if (!isOpen) {
            initialLoadDoneRef.current = false;
        }
    }, [isOpen]);

    // ── Unified search effect ──────────────────────────────────
    // First render after open → immediate full-list load.
    // Subsequent searchQuery changes → 300 ms debounce.
    useEffect(() => {
        if (!isOpen || !hasKeys) return;

        if (!initialLoadDoneRef.current) {
            initialLoadDoneRef.current = true;
            searchSolutions('');
            return;
        }

        const timer = setTimeout(() => {
            searchSolutions(searchQuery, 1, false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, isOpen, hasKeys, searchSolutions]);

    // ── Portal positioning ─────────────────────────────────────
    const updateDropdownPos = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - 16;
        setDropdownPos({
            top: rect.bottom + 8,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(200, Math.min(400, spaceBelow)),
        });
    }, []);

    useLayoutEffect(() => {
        if (isOpen) updateDropdownPos();
    }, [isOpen, updateDropdownPos]);

    useEffect(() => {
        if (!isOpen) return;
        const reposition = () => updateDropdownPos();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [isOpen, updateDropdownPos]);

    // ── Click-outside (accounts for portal) ────────────────────
    useEffect(() => {
        const handleClickOutside = (event) => {
            const inWrapper = wrapperRef.current?.contains(event.target);
            const inDropdown = dropdownRef.current?.contains(event.target);
            if (!inWrapper && !inDropdown) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Resolve pre-selected value ─────────────────────────────
    useEffect(() => {
        if (value && !selectedSolution && hasKeys) {
            const existing = solutions.find(s => String(s.id) === String(value));
            if (existing) {
                setSelectedSolution(existing);
            } else {
                threediumApi.getSolution(value)
                    .then(result => {
                        if (result.solution) setSelectedSolution(result.solution);
                    })
                    .catch(err => console.error('Failed to load solution:', err));
            }
        }
    }, [value, selectedSolution, solutions, hasKeys]);

    // ── Handlers ───────────────────────────────────────────────
    const handleSelect = (solution) => {
        setSelectedSolution(solution);
        onChange(String(solution.id));
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedSolution(null);
        onChange('');
    };

    const handleLoadMore = () => {
        if (!isLoading && hasMore) {
            searchSolutions(searchQuery, page + 1, true);
        }
    };

    // ── No-keys fallback ───────────────────────────────────────
    if (!hasKeys) {
        return (
            <div className="solution-picker no-keys">
                <label>Solution ID (Manual)</label>
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter Threedium Solution ID (e.g., 55757)"
                    disabled={disabled}
                    className="manual-input"
                />
                <p className="hint">
                    Configure Threedium API keys in your
                    <a href="/profile"> Profile Settings </a>
                    to browse solutions.
                </p>
            </div>
        );
    }

    // ── Portal dropdown content ────────────────────────────────
    const dropdownContent = isOpen && dropdownPos && (
        <div className="solution-picker">
            <div
                ref={dropdownRef}
                className="picker-dropdown picker-dropdown-portal"
                style={{
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    maxHeight: dropdownPos.maxHeight,
                }}
            >
                <div className="search-box">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by name or ID..."
                        autoFocus
                    />
                    {searchQuery && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchQuery('')}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    )}
                </div>

                <div className="solutions-list">
                    {error ? (
                        <div className="error-state">
                            <span><AlertIcon /> {formatErrorMessage(error)}</span>
                            <button onClick={() => searchSolutions(searchQuery)}>
                                Retry
                            </button>
                        </div>
                    ) : isLoading && solutions.length === 0 ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <span>Loading solutions...</span>
                        </div>
                    ) : solutions.length === 0 ? (
                        <div className="empty-state">
                            {searchQuery
                                ? `No solutions found for "${searchQuery}"`
                                : 'No solutions available'
                            }
                        </div>
                    ) : (
                        <>
                            {solutions.map((solution) => (
                                <div
                                    key={solution.id}
                                    className={`solution-item ${
                                        selectedSolution?.id === solution.id ? 'selected' : ''
                                    }`}
                                    onClick={() => handleSelect(solution)}
                                >
                                    <div className="solution-content">
                                        {solution.thumbnail_url && (
                                            <img
                                                src={solution.thumbnail_url}
                                                alt=""
                                                className="solution-thumb"
                                            />
                                        )}
                                        <div className="solution-details">
                                            <span className="solution-title">
                                                {solution.title}
                                            </span>
                                            {solution.description && (
                                                <span className="solution-description">
                                                    {solution.description.substring(0, 80)}
                                                    {solution.description.length > 80 ? '...' : ''}
                                                </span>
                                            )}
                                            <span className="solution-meta">
                                                ID: {solution.id}
                                                {solution.project_title && ` • ${solution.project_title}`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {hasMore && (
                                <button
                                    className="load-more-btn"
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Loading...' : 'Load More'}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────
    return (
        <div className="solution-picker" ref={wrapperRef}>
            <label>Threedium Solution</label>

            <div
                ref={triggerRef}
                className={`picker-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedSolution ? (
                    <div className="selected-solution">
                        <div className="solution-info">
                            <span className="solution-title">{selectedSolution.title}</span>
                            <span className="solution-id">ID: {selectedSolution.id}</span>
                        </div>
                        <button
                            className="clear-btn"
                            onClick={handleClear}
                            title="Clear selection"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <span className="chevron">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {isOpen ? (
                            <polyline points="18 15 12 9 6 15"/>
                        ) : (
                            <polyline points="6 9 12 15 18 9"/>
                        )}
                    </svg>
                </span>
            </div>

            {dropdownContent && createPortal(dropdownContent, document.body)}
        </div>
    );
}

export default ThreediumSolutionPicker;
